import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
	HIGHER_ORDER_PR_STATUSES,
	PURCHASE_REQUEST_STATUSES,
	PURCHASE_REQUEST_STATUS_DISPLAY_NAMES,
} from '../../constants/PurchaseRequestStatuses';
import { Button, Card, Popover, Steps, message } from 'antd';
import { purchaseRequestStatusChangeForSupplier } from '../../thunks/purchase_requests_thunks';
import { withRouter } from 'react-router';
import UpdateSerialAndAssetNumbersModal from '../purchase_orders_detail_details_page/UpdateSerialAndAssetNumbersModal';
import usePurchaseRequestFulfill from './usePurchaseRequestFulfill';

const PR_ACTIONS_LOCAL = {
	APPROVE: 'approve',
	REJECT: 'reject',
};

const PurchaseRequestStatusActions: FC<any> = ({
	purchaseRequest,
	updatePurchaseRequestStatus,
	onSuccess,
	match,
}): React.ReactElement => {
	const [localAction, setLocalAction] = useState(null);

	const {
		currentAction,
		PR_ACTIONS,
		equipmentLineItems,
		markFulfilledDisabledWithAssetTypeId,
		equipmentSerialNumberVisible,
		createdEntities,
		receivingEntityType,
		onOrdered,
		onFulfilled,
		hideSerialNumberUpdate,
		onUpdateSerialNumberSuccess,
		onReceiptValuesSuccessForPR,
	} = usePurchaseRequestFulfill(purchaseRequest, onSuccess);

	const prId = useMemo(() => match.params.id, [match.params.id]);

	const canShowActions = useMemo(
		() => HIGHER_ORDER_PR_STATUSES.includes(purchaseRequest.status),
		[purchaseRequest.status]
	);

	const currentPRIndex = useMemo(
		() => HIGHER_ORDER_PR_STATUSES.indexOf(purchaseRequest.status),
		[purchaseRequest.status]
	);

	const messageHeader = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return 'This purchase request is on hold pending your approval.';
			case PURCHASE_REQUEST_STATUSES.approved:
				return 'This purchase request is approved.';
			case PURCHASE_REQUEST_STATUSES.orderInProgress:
				return 'Order is in progress for this purchase request.';
			case PURCHASE_REQUEST_STATUSES.ordered:
				return 'This purchase request is ordered.';
			case PURCHASE_REQUEST_STATUSES.fulfilled:
				return 'This purchase request is fulfilled.';
			default:
				return '';
		}
	}, [purchaseRequest.status]);

	const messageDescriptin = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return 'Please review the details below and either approve the purchase request or deny it.';
			case PURCHASE_REQUEST_STATUSES.approved:
				return 'Do you want to mark this ordered?';
			case PURCHASE_REQUEST_STATUSES.orderInProgress:
				return 'Do you want to mark this ordered or has this been fulfilled?';
			case PURCHASE_REQUEST_STATUSES.ordered:
				return 'Is this purchase request fulfilled?';
			default:
				return '';
		}
	}, [purchaseRequest.status]);

	const onApprove = useCallback(() => {
		setLocalAction(PR_ACTIONS_LOCAL.APPROVE);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.approved)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setLocalAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const onReject = useCallback(() => {
		setLocalAction(PR_ACTIONS_LOCAL.REJECT);
		updatePurchaseRequestStatus(prId, PURCHASE_REQUEST_STATUSES.denied)
			.then(() => onSuccess && onSuccess())
			.catch((err) => message.error(err))
			.finally(() => setLocalAction(null));
	}, [onSuccess, prId, updatePurchaseRequestStatus]);

	const fulfilledButton = useMemo(
		() => (
			<Popover
				content={
					markFulfilledDisabledWithAssetTypeId
						? 'Please associate asset type with every equipment type in this purchase request!'
						: null
				}
				trigger="hover"
			>
				<Button
					key="reject"
					disabled={markFulfilledDisabledWithAssetTypeId}
					style={{ marginLeft: 16 }}
					loading={currentAction === PR_ACTIONS.FULFILL}
					onClick={onFulfilled}
				>
					Mark this fulfilled
				</Button>
			</Popover>
		),
		[currentAction, markFulfilledDisabledWithAssetTypeId, onFulfilled, PR_ACTIONS.FULFILL]
	);

	const prActions = useMemo(() => {
		switch (purchaseRequest.status) {
			case PURCHASE_REQUEST_STATUSES.requested:
				return (
					<div style={{ marginTop: 16 }}>
						<Button
							type="primary"
							key="approve"
							loading={localAction === PR_ACTIONS_LOCAL.APPROVE}
							onClick={onApprove}
						>
							Approve
						</Button>
						<Button
							key="reject"
							style={{ marginLeft: 16 }}
							onClick={onReject}
							loading={localAction === PR_ACTIONS_LOCAL.REJECT}
						>
							Deny
						</Button>
					</div>
				);
			case PURCHASE_REQUEST_STATUSES.approved:
				return (
					<div style={{ marginTop: 16 }}>
						<Button
							type="primary"
							key="approve"
							loading={currentAction === PR_ACTIONS.ORDER}
							onClick={onOrdered}
						>
							Mark this ordered
						</Button>
						{fulfilledButton}
					</div>
				);
			case PURCHASE_REQUEST_STATUSES.ordered:
				return <div style={{ marginTop: 16 }}>{fulfilledButton}</div>;
			default:
				return null;
		}
	}, [
		currentAction,
		fulfilledButton,
		localAction,
		onApprove,
		onOrdered,
		onReject,
		purchaseRequest.status,
		PR_ACTIONS.ORDER,
	]);

	return canShowActions ? (
		<Card>
			<div className="flex flex-col">
				<div className="mb-4">
					<Steps current={currentPRIndex}>
						{HIGHER_ORDER_PR_STATUSES.map((status) => (
							<Steps.Step key={status} title={PURCHASE_REQUEST_STATUS_DISPLAY_NAMES[status]} />
						))}
					</Steps>
				</div>
				<h5>{messageHeader}</h5>
				<div style={{ fontSize: 16, maxWidth: 640 }}>{messageDescriptin}</div>
				<div>{prActions}</div>
			</div>
			{equipmentSerialNumberVisible && (
				<UpdateSerialAndAssetNumbersModal
					createdEntities={createdEntities}
					entityType={receivingEntityType}
					onSuccess={onUpdateSerialNumberSuccess}
					onCancel={hideSerialNumberUpdate}
					prLineItems={equipmentLineItems}
					onReceiptValuesSuccessForPR={onReceiptValuesSuccessForPR}
				/>
			)}
		</Card>
	) : null;
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	updatePurchaseRequestStatus: (id, status, equipmentReceiptValues = undefined) =>
		dispatch(purchaseRequestStatusChangeForSupplier(id, status, equipmentReceiptValues)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(PurchaseRequestStatusActions)
);
